// Resource Permission Service — calls hub proxy for generic resource permission endpoints.
// Endpoints: GET /v1/cms/permissions/check, POST /grant, POST /revoke, GET /resource, GET /audit.
import { hubCall } from "@/lib/api/hub";
import type { ApiResponse, PaginatedResponse, UserSummary } from "@/types/api.types";

/** Slim permission_groups projection mirroring BE resourcepermdto.GroupSummary. */
export interface PermissionGroupSummary {
  id: string;
  name: string;
  groupType: "dept" | "access_group";
}

export type ResourceType =
  | "knowledge"
  | "folder"
  | "media_asset"
  | "connector"
  | "workflow"
  | "workflow_run"
  | "agent_task"
  | "kg_entity"
  | "kg_relation";

export type PermAction = "read" | "write" | "admin";
export type PermissionSource = "direct" | "folder_inherited";

export interface PermCheckResult {
  allowed: boolean;
}

export interface ResourcePermissionRow {
  id: string;
  resourceType?: ResourceType;
  resourceId?: string;
  principalKind: PrincipalKind;
  /** Present when principalKind ∈ {user, group}. */
  principalId?: string;
  /** Present when principalKind === 'role'. */
  principalRole?: string;
  /** Slim user projection — present only when principalKind === 'user'. */
  principalUser?: UserSummary | null;
  /** Slim group projection — present only when principalKind === 'group'. */
  principalGroup?: PermissionGroupSummary | null;
  action: string;
  grantedBy: string;
  createdAt: string;
  source?: PermissionSource;
  sourceResourceType?: ResourceType | "folder";
  sourceResourceId?: string;
  sourceName?: string | null;
  editable?: boolean;
}

/**
 * 4-principal payload shape (Phase 10).
 *   - kind='user'  → principalId = users.id UUID
 *   - kind='group' → principalId = permission_groups.id (department or access_group)
 *   - kind='role'  → principalRole = OrgRole string ('owner'|'admin'|'member'|'viewer')
 *
 * BE enforces XOR via `chk_principal_xor` CHECK constraint and
 * validatePrincipalPayload at modules/resourceperm/service/perm_service_helpers.go.
 */
export type PrincipalKind = "user" | "group" | "role";

export interface GrantRevokePayload {
  resourceType: ResourceType;
  resourceId: string;
  principalKind: PrincipalKind;
  /** UUID — required when principalKind ∈ {user, group}. */
  principalId?: string;
  /** Role slug — required when principalKind === 'role'. */
  principalRole?: string;
  action: PermAction;
}

/**
 * Map FE singular `action` → BE `actions: [...]` array.
 * All other fields are already camelCase matching the BE wire tags.
 */
function toBeWireShape(p: GrantRevokePayload): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    resourceType: p.resourceType,
    resourceId: p.resourceId,
    principalKind: p.principalKind,
    actions: [p.action],
  };
  if (p.principalId !== undefined) wire.principalId = p.principalId;
  if (p.principalRole !== undefined) wire.principalRole = p.principalRole;
  return wire;
}

function buildQS(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const resourcePermissionService = {
  /** GET /v1/cms/permissions/check — returns {allowed: boolean}. Never throws 403. */
  async check(resourceType: ResourceType, resourceId: string, action: PermAction): Promise<PermCheckResult> {
    // URL query params stay snake per phase-00 URL Query Convention.
    const qs = buildQS({ resource_type: resourceType, resource_id: resourceId, action });
    return hubCall<PermCheckResult>(`/permissions/check${qs}`);
  },

  /** GET /v1/cms/permissions/check-default checks org_resource_default_perms. */
  async checkDefault(resourceType: ResourceType, action: PermAction): Promise<PermCheckResult> {
    const qs = buildQS({
      resource_type: resourceType,
      action,
    });
    return hubCall<PermCheckResult>(`/permissions/check-default${qs}`);
  },

  /**
   * POST /v1/cms/permissions/grant
   *
   * Maps camelCase FE payload to BE wire shape (camelCase body + actions array).
   * BE rejects unknown fields via Gin binding so the mapping must be exact.
   */
  async grant(payload: GrantRevokePayload): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/permissions/grant", {
      method: "POST",
      body: JSON.stringify(toBeWireShape(payload)),
    });
  },

  /** POST /v1/cms/permissions/revoke — same wire-shape mapping as grant. */
  async revoke(payload: GrantRevokePayload): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/permissions/revoke", {
      method: "POST",
      body: JSON.stringify(toBeWireShape(payload)),
    });
  },

  /** GET /v1/cms/permissions/resource?resource_type=...&resource_id=... */
  async listByResource(
    resourceType: ResourceType,
    resourceId: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<ResourcePermissionRow>> {
    // URL query params stay snake per phase-00 URL Query Convention.
    const qs = buildQS({ resource_type: resourceType, resource_id: resourceId, ...params });
    const raw = await hubCall<PaginatedResponse<ResourcePermissionRow>>(`/permissions/resource${qs}`);
    // BE now emits camelCase — rows map directly; remap grantedAt → createdAt for UI consistency.
    return {
      ...raw,
      data: (raw.data ?? []).map((r) => ({
        ...r,
        // BE field `grantedAt` is mapped to `createdAt` on the FE interface.
        createdAt: (r as unknown as { grantedAt?: string }).grantedAt ?? r.createdAt ?? "",
      })),
    };
  },
};
